/**
 * @file StatementStepContents.js
 * @version 2.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 *
 * Generate Statements, step 2 — Contents: everything about what is inside
 * each PDF, chosen BEFORE review so every preview is what goes (Cam, 20 Oct
 * 2026: "some of the options before emailing and printing regarding what is
 * included … should be on a screen prior to the sending step, as previewing
 * should reflect what will actually send/print"). A sample customer's PDF
 * is drawn beside the choices and follows them. Where it goes (email,
 * print, office, ZIP) stays on Send.
 *
 * Fully-used credit notes (Cam: "Users should be able to decide if they want
 * to include fully allocated credit notes explicitly listed on the statement
 * or with the invoices"): two separate switches, both off unless chosen.
 *
 * 2.0.0 (2026-10-20, Cam — the statements look): a settings list — a
 * segmented control for the documents, option cards for unpaid invoices, the
 * app's toggle switches, Extra PDFs as a list with "Add PDFs".
 * 2.1.0 (2026-10-20, Cam — statement notes): the sample is drawn again when its
 * customer note changes, and says when that note is on it (the office note is
 * never on a statement).
 */

import React, { useMemo, useState } from 'react';
import StatementPdfPane from './StatementPdfPane';
import { accountLabel } from '../utils/statementOrder';
import { NOTE_FIELDS, hasCustomerNote, previewNoteKey } from '../utils/statementNotes';

const INCLUDE = [
  { value: 'both', label: 'Statement and invoices' },
  { value: 'statement', label: 'Statement only' },
  { value: 'invoices', label: 'Invoices only' }
];

const UNPAID = [
  { value: false, title: 'Aged summary', text: 'One line per month that is overdue' },
  { value: true, title: 'Listed, with their PDFs', text: 'Each unpaid invoice on its own line, with its PDF attached' }
];

/** The app's toggle switch, labelled by its row's words. */
function ToggleRow({ id, checked, onChange, children, hint }) {
  return (
    <div className="sr-toggle-row">
      <label className="sr-toggle-text" htmlFor={id}>
        <span>{children}</span>
        {hint && <small>{hint}</small>}
      </label>
      <div className="checkbox-container">
        <div className="check light">
          <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
          <label htmlFor={id}></label>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.content - { include, includeAllUnpaidInvoices, suppressOverdueStamp, listUsedCredits, attachUsedCredits }
 * @param {function} props.onContent - (key, value)
 * @param {Array} props.statements
 * @param {boolean} props.interim
 * @param {object} props.attachments - { shown, busy, onChoose, onRemove, meId, keptWithRun }
 * @param {'email'|'print'} props.template
 * @param {function} props.onTemplate
 */
function StatementStepContents({ content, onContent, statements, interim, attachments, template, onTemplate }) {
  // The sample: a customer who shows the choices best (one with fully-used
  // credit notes while those are on, else one with invoices), until one is picked.
  const suggested = useMemo(() => {
    const withUsed = statements.find(s => s.usedCreditCount > 0);
    const withInvoices = statements.find(s => s.invoiceCount > 0);
    if ((content.listUsedCredits || content.attachUsedCredits) && withUsed) return withUsed.id;
    return (withInvoices || statements[0] || {}).id || null;
  }, [statements, content.listUsedCredits, content.attachUsedCredits]);
  const [picked, setPicked] = useState(null);
  const sampleId = picked && statements.some(s => s.id === picked) ? picked : suggested;
  const sample = statements.find(s => s.id === sampleId) || null;
  const usedCount = statements.reduce((n, s) => n + (s.usedCreditCount || 0), 0);
  const usedCustomers = statements.filter(s => s.usedCreditCount > 0).length;

  return (
    <div className="sr-contents">
      <section className="sr-panel sr-contents-choices">
        <div className="sr-card-head">
          <div>
            <h4 className="sr-card-title">Each PDF contains</h4>
            <p className="sr-card-sub">The sample beside follows these choices</p>
          </div>
        </div>
        <div className="sr-card-body">
          <div className="sr-section">
            <span className="sr-label">Documents</span>
            <p className="sr-help">Which customers&rsquo; invoices are bundled can still be changed per statement on Review.</p>
            <div className="sr-seg wide" role="radiogroup" aria-label="Documents">
              {INCLUDE.map(o => (
                <button key={o.value} type="button" role="radio" aria-checked={content.include === o.value}
                  className={content.include === o.value ? 'on' : ''} onClick={() => onContent('include', o.value)}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="sr-section">
            <span className="sr-label">Unpaid invoices from earlier months</span>
            <p className="sr-help">How invoices from before the month that are still owed appear.</p>
            <div className="sr-options" role="radiogroup" aria-label="Unpaid invoices from earlier months">
              {UNPAID.map(o => {
                const on = !!content.includeAllUnpaidInvoices === o.value;
                return (
                  <button key={String(o.value)} type="button" role="radio" aria-checked={on}
                    className={`sr-option${on ? ' on' : ''}`} onClick={() => onContent('includeAllUnpaidInvoices', o.value)}>
                    <span className="sr-radio" aria-hidden="true" />
                    <span>
                      <span className="sr-option-title">{o.title}</span>
                      <span className="sr-option-text">{o.text}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="sr-section">
            <span className="sr-label">
              Fully-used credit notes
              {statements.length > 0 && <span className="sr-badge soft neutral">{usedCount} in this run</span>}
            </span>
            <p className="sr-help">
              Credit notes in the month that were allocated or refunded in full by its end.
              {statements.length > 0 && (usedCount
                ? ` This run has ${usedCount} across ${usedCustomers} customer${usedCustomers === 1 ? '' : 's'}.`
                : ' This run has none.')}
              {' '}Usually left off a month-end statement; useful when a customer needs to see how a credit was used.
            </p>
            <ToggleRow id="sr-list-used" checked={!!content.listUsedCredits} onChange={(v) => onContent('listUsedCredits', v)}
              hint="Shown among the month's lines with what each was used against; the balance does not change">
              List them on the statement
            </ToggleRow>
            <ToggleRow id="sr-attach-used" checked={!!content.attachUsedCredits} onChange={(v) => onContent('attachUsedCredits', v)}
              hint="Their PDFs go after the invoices">
              Attach their PDFs with the invoices
            </ToggleRow>
          </div>

          <div className="sr-section">
            <ToggleRow id="sr-overdue" checked={!!content.suppressOverdueStamp} onChange={(v) => onContent('suppressOverdueStamp', v)}
              hint="The stamp shows on invoices past their due date">
              Leave the OVERDUE stamp off the invoices
            </ToggleRow>
          </div>

          <div className="sr-section">
            <span className="sr-label">Extra PDFs <span className="sr-badge soft neutral"><span className="material-symbols-rounded">mail</span>Emailed only</span></span>
            <p className="sr-help">Sent with every emailed statement — a price list, a letter.</p>
            {attachments.shown.length > 0 && (
              <div className="sr-files">
                {attachments.shown.map((file, idx) => (
                  <div key={file.id || `local-${idx}`} className="sr-file">
                    <span className="material-symbols-rounded">picture_as_pdf</span>
                    <span className="sr-name">{file.name}</span>
                    <span className="sr-muted">{file.byName && file.by !== attachments.meId ? `added by ${file.byName}` : ''}</span>
                    <button onClick={() => attachments.onRemove(file)} className="btn-icon" title="Take this PDF off">
                      <span className="material-symbols-rounded">close</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="buttons slideout">
              <div>
                <button type="button" className="btn-slideout always-out" onClick={() => document.getElementById('file-input-generate').click()}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">upload_file</span></div>
                  <span className="btn-label">Add PDFs</span>
                </button>
              </div>
            </div>
            <input
              id="file-input-generate"
              data-testid="generate-pdf-input"
              type="file"
              multiple
              accept=".pdf"
              onChange={(e) => { attachments.onChoose(Array.from(e.target.files)); e.target.value = ''; }}
              style={{ display: 'none' }}
            />
            {attachments.busy && <p className="sr-hint"><span className="material-symbols-rounded">sync</span>Keeping them with the run…</p>}
          </div>

          {interim && (
            <p className="sr-hint">
              <span className="material-symbols-rounded">history_edu</span>
              Interim: each statement is titled &ldquo;(Interim)&rdquo; and ends with what has changed since the month end.
            </p>
          )}
        </div>
      </section>

      <section className="sr-panel sr-contents-sample">
        <StatementPdfPane
          statementId={sampleId}
          noteKey={previewNoteKey(sample?.note)}
          content={content}
          template={template}
          onTemplate={onTemplate}
          emptyText="Generate the statements to see a sample here."
          notice={hasCustomerNote(sample) ? (
            <p className="sr-preview-note customer" title={`Note for the customer — printed on the statement and in the email: ${String(sample.note).trim()}`}>
              <span className="material-symbols-rounded">{NOTE_FIELDS.note.icon}</span>
              <span><strong>On the statement and in the email:</strong> {String(sample.note).trim()}</span>
            </p>
          ) : null}
          heading={(
            <label className="sr-sample-pick">
              <span className="sr-muted">Sample</span>
              <select value={sampleId || ''} onChange={(e) => setPicked(Number(e.target.value) || null)}>
                {statements.map(s => (
                  <option key={s.id} value={s.id}>
                    {accountLabel(s.accountNo, s.account)}{s.usedCreditCount ? ` · ${s.usedCreditCount} used credit${s.usedCreditCount === 1 ? '' : 's'}` : ''}{hasCustomerNote(s) ? ' · note on statement' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
        />
      </section>
    </div>
  );
}

export default StatementStepContents;
